import { useMemo, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ProjectRole } from '@courseai/domain';
import { useAuth } from '../auth/AuthContext';
import { useT, type TranslationKey } from '../i18n';
import {
  GET_PROJECT,
  type ProjectDetailData,
  type GetProjectVariables,
} from '../graphql/project-detail';

// Context shared with the tab child routes via <Outlet context>. Tabs read the
// resolved projectId and the caller's role so each can decide what to show
// (e.g. Colaboradores hides management controls from non-owners, Req 3.2).
export interface ProjectTabContext {
  readonly projectId: string;
  readonly role: ProjectRole | undefined;
  /** True when the caller may perform create/update/generate operations. */
  readonly canEdit: boolean;
  /** True when the caller may manage members (owner-only). */
  readonly canManage: boolean;
}

// The tab surface for the Project detail screen. Each entry maps to a nested
// route declared in routes.tsx. Cursos is the index tab. The ownership of tab
// *content* is split across tasks: Cursos → 16.4/16.5, Perfiles de voz +
// Estilos visuales → 16.6, Generaciones → 16.7; Colaboradores is implemented
// here in 16.3.
const TABS: ReadonlyArray<{ to: string; labelKey: TranslationKey; end: boolean }> = [
  // `end` makes the index (Courses) tab active only on the exact project path,
  // so it does not stay highlighted while a sibling tab is selected.
  { to: '', labelKey: 'project.tab.courses', end: true },
  { to: 'drafts', labelKey: 'project.tab.drafts', end: false },
  { to: 'voice-profiles', labelKey: 'project.tab.voiceProfiles', end: false },
  { to: 'art-styles', labelKey: 'project.tab.artStyles', end: false },
  { to: 'generations', labelKey: 'project.tab.generations', end: false },
  { to: 'collaborators', labelKey: 'project.tab.collaborators', end: false },
];

// Project detail screen: a header (project name) plus a tab navigation and a
// nested <Outlet /> where the active tab renders. The tabbed surface covers the
// Cursos, Perfiles de voz, Estilos visuales, Generaciones and Colaboradores
// areas (Req 3.1, 3.2, 4.1, 10.1, 10.3). Header/members come from `getProject`.
export function ProjectDetail(): ReactNode {
  const { projectId } = useParams<{ projectId: string }>();
  const auth = useAuth();
  const { t } = useT();

  const { data, loading, error } = useQuery<ProjectDetailData, GetProjectVariables>(GET_PROJECT, {
    variables: { projectId: projectId ?? '' },
    skip: projectId === undefined,
    fetchPolicy: 'cache-and-network',
  });

  const project = data?.getProject ?? undefined;

  // Resolve the caller's role from the membership list the API returns. The
  // API derives identity from the JWT; auth context exposes the same userId for
  // UI decisions only.
  const role = useMemo<ProjectRole | undefined>(() => {
    const userId = auth.user?.userId;
    if (project === undefined || userId === undefined) {
      return undefined;
    }
    return project.members.find((member) => member.userId === userId)?.role;
  }, [project, auth.user?.userId]);

  const tabContext = useMemo<ProjectTabContext>(
    () => ({
      projectId: projectId ?? '',
      role,
      canEdit: role === ProjectRole.owner || role === ProjectRole.editor,
      canManage: role === ProjectRole.owner,
    }),
    [projectId, role],
  );

  if (projectId === undefined) {
    return (
      <section className="screen">
        <p className="form-error">{t('project.notSpecified')}</p>
      </section>
    );
  }

  return (
    <section className="screen">
      <p>
        <Link to="/">{t('project.backToProjects')}</Link>
      </p>

      <div className="screen-header">
        <h2>{project?.name ?? (loading ? t('app.loading') : t('project.fallbackTitle'))}</h2>
      </div>

      {error && <p className="form-error">{t('project.loadError', { message: error.message })}</p>}

      <nav className="tab-nav" aria-label={t('project.sectionsNav')}>
        {TABS.map((tab) => (
          <NavLink
            key={tab.to || 'index'}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => (isActive ? 'tab-link tab-link-active' : 'tab-link')}
          >
            {t(tab.labelKey)}
          </NavLink>
        ))}
      </nav>

      <Outlet context={tabContext} />
    </section>
  );
}
